import { IPlayerState } from '../IPlayerState';
import { IPlayerLevelData, LevelType } from './PlayerLevels';
import { PlayerStateHelper } from '../PlayerStateHelper';
import { Immutable } from 'Utility/Types';
import { IGameState } from 'State/Game/IGameState';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { ILimit, MathHelper } from 'Utility/MathHelper';
import IPlayerProgressData from 'Player/PlayerProperty/Progress/IPlayerProgressData';
import DateHelper from 'Utility/DateHelper';
import { TimerType } from 'System/Timer/TimerTypes';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import { ADS_BEFORE_COOLDOWN } from 'Constants/Ads';
import { PlayerTask } from '../PlayerTasks';
import { PopupID } from 'State/UI/PopupID';

export namespace ProgressStateHelper {
    /** Returns the number of stars acquired in the given level.
     * @param levelNumber
     */
    export const getLevelStars = (
        state: IGameState,
        levelNumber: number,
        levelType: LevelType = LevelType.Core
    ): number => {
        return getLevelData(state, levelNumber, levelType)?.stars || 0;
    };

    export const getLevelData = (
        state: Immutable<IGameState>,
        levelNumber: number,
        levelType: LevelType = LevelType.Core
    ): Immutable<IPlayerLevelData> => {
        return state.playerState.levels[levelType].find(levelData => levelData.levelNumber === levelNumber);
    };

    /**
     * Upsert a level into the player state.
     * @param state
     * @param levelNumber Required, unique level number to insert or update.
     * @param levelData Partial level data set to upsert with.
     */
    export const updateLevel = (
        state: IGameState,
        levelNumber: number,
        levelData: Partial<IPlayerLevelData>,
        levelType: LevelType = LevelType.Core
    ): IGameState => {
        let updatedLevels: IPlayerLevelData[];
        // If the level is not found, add it.
        if (getLevelData(state, levelNumber, levelType) === undefined) {
            updatedLevels = [
                ...state.playerState.levels[levelType],
                {
                    helperFriendAttempts: 0,
                    ...levelData,
                    // Level number appended at the end just to be sure.
                    levelNumber,
                },
            ];
        } else {
            updatedLevels = state.playerState.levels[levelType].map(level => {
                if (level.levelNumber !== levelNumber) {
                    return level;
                }
                return {
                    ...level,
                    ...levelData,
                    levelNumber,
                };
            });
        }

        return {
            ...state,
            playerState: {
                ...state.playerState,
                levels: {
                    ...state.playerState.levels,
                    [levelType]: updatedLevels,
                },
            },
        };
    };

    export const unlockNextWorld = (
        oldPlayerState: IPlayerState,
        forceSuppressFarmItemUnlockPopup: boolean = false
    ): IPlayerState => {
        let playerState = oldPlayerState;
        const timers = new Map(playerState.timers);

        playerState = {
            ...playerState,
            maxWorld: playerState.maxWorld + 1,
            timers,
            social: {
                ...playerState.social,
            },
        };

        // Queue farm items unlock popup
        if (!forceSuppressFarmItemUnlockPopup) {
            playerState = PlayerStateHelper.addStateTaskDatas(playerState, [
                {
                    task: PlayerTask.SHOW_POPUP,
                    popup: PopupID.FARM_UNLOCK_ITEMS,
                    meta: {},
                },
            ]);
        }

        return playerState;
    };

    /**
     * Get the total number of stars the player has earned in the specified world.
     * @param state
     * @param worldNumber
     */
    export const getWorldStarsTotal = (
        state: IPlayerState,
        worldNumber: number,
        levelType: LevelType = LevelType.Core
    ): number => {
        const worldLimits: ILimit = WorldDataHelper.getWorldLevelLimits(worldNumber);
        return state.levels[levelType].reduce(
            (a, b) => (MathHelper.isWithinRange(b.levelNumber, worldLimits.min, worldLimits.max) ? a + b.stars : a),
            0
        );
    };

    /**
     * Get the total number of stars earned by the player.
     * @param state
     */
    export const getStarsCount = (state: IPlayerState, levelType: LevelType = LevelType.Core): number => {
        return state.levels[levelType].reduce((a, b) => a + (b.stars || 0), 0);
    };

    export const setCurrencyShareCount = (playerState: IPlayerState, count: number): IPlayerState => {
        return setProgress(playerState, 'currencyShareCount', count);
    };

    export const canUnlockNextWorld = (state: IGameState, highestLevelCompleted: number): boolean => {
        return (
            highestLevelCompleted >= WorldDataHelper.maxLevel(state.playerState.maxWorld) &&
            state.playerState.maxWorld < WorldDataHelper.maxWorld()
        );
    };

    export const setSpinCount = (state: IPlayerState, value: number): IPlayerState => {
        return setProgress(state, 'wheelSpinCount', value);
    };

    export const increaseTotalAdsWatched = (state: IGameState): IGameState => {
        const totalAdsWatched = state.playerState.progress.totalAdsWatched + 1;
        return PlayerStateHelper.setPlayerState(
            state,
            setProgress(state.playerState, 'totalAdsWatched', totalAdsWatched)
        );
    };

    export const setShopGiftUses = (state: IPlayerState, value: number): IPlayerState => {
        return setProgress(state, 'shopGiftsCollected', value);
    };

    export const setAdsWatched = (oldState: IGameState, value: number): IGameState => {
        let state = { ...oldState };
        // Start timer if
        if (value >= ADS_BEFORE_COOLDOWN) {
            state = TimerStateHelper.setTimer(state, TimerType.AD_COOLDOWN, DateHelper.now);
        }
        return PlayerStateHelper.setPlayerState(state, setProgress(state.playerState, 'adsWatched', value));
    };

    // TODO: Unused??
    export const setLevelsSinceAd = (state: IGameState, value: number): IGameState => {
        return PlayerStateHelper.setPlayerState(state, setProgress(state.playerState, 'levelsSinceAd', value));
    };

    export const setProgress = (
        state: IPlayerState,
        key: keyof IPlayerProgressData,
        value: IPlayerProgressData[typeof key]
    ): IPlayerState => {
        return {
            ...state,
            progress: {
                ...state.progress,
                [key]: value,
            },
        };
    };
}
